function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PureComponent } from "react";
import classnames from "classnames";
import sanitizeHtml from "sanitize-html";
import stringUtils from "utils/strings";
import colors from "styles/colors";
import { NUM_DEFAULT_DISPLAYED_PLANS, PERIOD_TEXTS, TIERS } from "constants/pricing";
import { withGenericConfig } from "/contexts/generic-config-context";
import { preventFocusOnMouseDown } from "client/services/accessibility/accessibility-service";
import { getCurrencyWithPriceDisplay, shouldSeeContactUs } from "/services/pricing-service";
import { hasLearnMoreVideo } from "/services/pricing-learn-more-service";
import { getPlanColor, getPreviousTierName } from "services/pricing/plans-service";
import { getMaxFreeUsersFromClusterConfig, shouldHideCrossedOutPrice } from "/services/pricing/discounts-service";
import { shouldShowYearlyPrices } from "/services/pricing/periods-service";
import { getMostPopularTierAndText } from "/services/pricing/pricing-page/most-popular-service";
import { getPricingDimensions } from "/services/pricing/pricing-page/pricing-page-display-service";
import { getFreeTierSeatsCountByAbTest } from "services/ab-tests/monetization-ab-tests-service";
import PricingActionButton from "segments/desktop/core-components/pricing-action-button/pricing-action-button";
import { REGULAR_BUTTON_TYPE, SECONDARY_BUTTON_TYPE } from "segments/desktop/core-components/button/button-consts";
import Check from "monday-ui-react-core/dist/icons/Check";
import { getVideoIdByLocaleAndTier } from "services/pricing-learn-more-service";
import YouTubePopUpButton from "segments/desktop/core-components/button/youtube-popup-button/youtube-popup-button";
import { WORK_OS_IRIS_COLOR_NAME } from "styles/color-consts";
import FeatureInfoWithTooltip from "./feature-info-with-tooltip/feature-info-with-tooltip";
import ContactUsImage from "./contact-us-image/contact-us-image";
import MondayAiIcon from "components/core/monday-ai-icon/monday-ai-icon-component";
import { isAIFeature } from "constants/pricing/pricing-page/features/value_features";
import { StyledFreeTierTotalPrice, StyledMostPopular, StyledPerSeatPerMonthText, StyledPriceAndFeaturesCard, StyledPriceWithoutDiscount, StyledRegularTotalPriceSection, StyledTierFeature, StyledTierName } from "./price-and-features-card.scss";
const PRICE_CARD_PRICE_CONTAINER_CLASS = "price-card-price-container";
class PriceAndFeaturesCard extends PureComponent {
    getTierIncludesText() {
        const { plan, plans, translateOrTranslateWithParam } = this.props;
        const previousTierName = getPreviousTierName(plan.tier, plans);
        return previousTierName == null ? translateOrTranslateWithParam({
            key: this.getRegularOrLineDropText("pricingPage.tierCards.planIncludes"),
            param: translateOrTranslateWithParam(plan.displayName)
        }) : translateOrTranslateWithParam({
            key: this.getRegularOrLineDropText("pricingPage.tierCards.includesPlanPlus"),
            param: translateOrTranslateWithParam(previousTierName)
        });
    }
    getLearnMoreButtonText() {
        const { plan, translateOrTranslateWithParam } = this.props;
        return translateOrTranslateWithParam({
            key: "pricingPage.tierCards.learnMoreButton",
            param: translateOrTranslateWithParam(plan.displayName)
        });
    }
    render() {
        const { plan, isChosen, translateOrTranslateWithParam, features, userCount, tierDescription, signupButtonText, contactUsButtonText, plans, isMobile, cookies, forcePricing, abTests, billingPeriod, showLearnMoreVideo, hidePlanTryForFreeButton } = this.props;
        const clusterConfig = this.getClusterConfigOrDefault();
        const tierIncludesText = this.getTierIncludesText();
        const isContactUsCard = shouldSeeContactUs(plan.tier, userCount, {
            clusterConfig,
            cookies,
            forcePricing,
            abTests
        });
        const { tierCards: tierCardsDimensions } = getPricingDimensions(plans);
        const { planWidth } = tierCardsDimensions;
        const tierCardStyle = {
            width: `${isMobile ? 100 : planWidth}%`
        };
        const shouldShowLearnMoreVideo = showLearnMoreVideo && hasLearnMoreVideo(plan.tier);
        return /*#__PURE__*/ _jsxs(StyledPriceAndFeaturesCard, {
            className: classnames("price-and-features-card", {
                chosen: isChosen,
                "hide-ctas": hidePlanTryForFreeButton
            }),
            style: tierCardStyle,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "tier-color-bar",
                    style: {
                        backgroundColor: plan.tier === TIERS.FREE ? "transparent" : getPlanColor(plan, false)
                    }
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: PRICE_CARD_PRICE_CONTAINER_CLASS,
                    role: "region",
                    "aria-labelledby": `price-card-aria-${plan === null || plan === void 0 ? void 0 : plan.tier}`,
                    children: [
                        this.renderTierName(),
                        /*#__PURE__*/ _jsx("div", {
                            className: classnames("pricing-section", {
                                "contact-us": isContactUsCard
                            }),
                            children: isContactUsCard ? /*#__PURE__*/ _jsx(ContactUsImage, {
                                plan: plan
                            }) : this.renderPriceSection()
                        }),
                        this.shouldShowCTA() && /*#__PURE__*/ _jsx("div", {
                            className: "try-cta",
                            children: /*#__PURE__*/ _jsx(PricingActionButton, {
                                plan: plan,
                                userCount: userCount,
                                placement: "price-and-features-card",
                                signupButtonText: translateOrTranslateWithParam(signupButtonText),
                                contactUsButtonText: translateOrTranslateWithParam(contactUsButtonText),
                                billingPeriod: billingPeriod
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: classnames("tier-description", {
                                "with-learn-more-button": shouldShowLearnMoreVideo
                            }),
                            tabIndex: 0,
                            onMouseDown: preventFocusOnMouseDown,
                            children: translateOrTranslateWithParam(tierDescription)
                        }),
                        showLearnMoreVideo && this.renderLearnMoreVideoCTAOrPlaceholder()
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: classnames("separator", {
                        "with-added-margin": this.shouldIncreaseSeparatorTopMargin()
                    }),
                    style: {
                        borderColor: colors["wolf"]
                    }
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "tier-includes-container",
                    children: [
                        /*#__PURE__*/ _jsx("h3", {
                            className: "tier-includes-title",
                            dangerouslySetInnerHTML: {
                                __html: tierIncludesText
                            }
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "tier-includes-list",
                            children: features.map(this.renderTierFeature)
                        })
                    ]
                })
            ]
        });
    }
    constructor(props){
        super(props);
        _define_property(this, "hasMoreThanFourPlans", ()=>{
            const { plans } = this.props;
            return plans.length > NUM_DEFAULT_DISPLAYED_PLANS;
        });
        _define_property(this, "getClusterConfigOrDefault", ()=>{
            const { ignoreClusterConfig } = this.props;
            return ignoreClusterConfig ? {
                clusterConfig: {}
            } : this.props.clusterConfig;
        });
        _define_property(this, "getPricePerSeat", (price)=>{
            const { userCount, cookies, forcePricing, abTests } = this.props;
            const clusterConfig = this.getClusterConfigOrDefault();
            const freeUsersCount = getMaxFreeUsersFromClusterConfig(clusterConfig, abTests, cookies, forcePricing) || 0;
            if (freeUsersCount >= userCount) return 0;
            return 100 * price / (userCount - freeUsersCount) / 100;
        });
        _define_property(this, "renderPricePerSeatWithoutDiscountIfNeeded", ()=>{
            const { currencyValue, localeId, planPriceWithoutDiscount, planPrice, cookies, forcePricing, abTests } = this.props;
            const clusterConfig = this.getClusterConfigOrDefault();
            if (shouldHideCrossedOutPrice(clusterConfig, abTests, cookies, forcePricing)) return;
            if (planPrice === planPriceWithoutDiscount) return;
            const priceWithoutDiscount = getCurrencyWithPriceDisplay({
                price: this.getPricePerSeat(planPriceWithoutDiscount).toLocaleString(),
                currency: currencyValue,
                localeId
            });
            return /*#__PURE__*/ _jsx(StyledPriceWithoutDiscount, {
                className: "plan-price-without-discount",
                children: /*#__PURE__*/ _jsx("span", {
                    className: "plan-price-without-discount-inner",
                    children: priceWithoutDiscount
                })
            });
        });
        _define_property(this, "renderPricePerSeat", ()=>{
            const { currencyValue, localeId, planPrice } = this.props;
            return getCurrencyWithPriceDisplay({
                price: this.getPricePerSeat(planPrice).toLocaleString(),
                currency: currencyValue,
                localeId
            });
        });
        _define_property(this, "renderFullPrice", ()=>{
            const { currencyValue, planPrice, localeId } = this.props;
            return getCurrencyWithPriceDisplay({
                price: planPrice.toLocaleString(),
                currency: currencyValue,
                localeId,
                hideCurrency: true
            });
        });
        _define_property(this, "renderMostPopularIfNeeded", ()=>{
            const { plan, translateOrTranslateWithParam, userCount, plans, cookies, forcePricing, abTests } = this.props;
            const clusterConfig = this.getClusterConfigOrDefault();
            const planColor = getPlanColor(plan, true);
            const isSquareTopText = this.hasMoreThanFourPlans();
            const { tier: mostPopularTier, text: mostPopularText, minimumUsersCount: mostPopularMinimumUsersCount } = getMostPopularTierAndText(userCount, plans, clusterConfig, abTests, cookies, forcePricing);
            const { tier } = plan;
            if (mostPopularTier !== tier) return;
            if (mostPopularMinimumUsersCount && userCount < mostPopularMinimumUsersCount) return;
            return /*#__PURE__*/ _jsxs(StyledMostPopular, {
                id: tier,
                className: classnames("tier-name-extra", {
                    "with-square-top-text": isSquareTopText
                }),
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "arrow",
                        style: {
                            borderRightColor: planColor
                        }
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "extra-text",
                        style: {
                            backgroundColor: planColor
                        },
                        children: translateOrTranslateWithParam(mostPopularText)
                    })
                ]
            });
        });
        _define_property(this, "renderTierName", ()=>{
            const { plan, translateOrTranslateWithParam } = this.props;
            return /*#__PURE__*/ _jsxs(StyledTierName, {
                className: "price-card-tier-name",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "tier-name-text",
                        id: `tier-name-${plan === null || plan === void 0 ? void 0 : plan.tier}`,
                        children: stringUtils.getCapitilizedString(translateOrTranslateWithParam(plan.displayName))
                    }),
                    this.renderMostPopularIfNeeded()
                ]
            });
        });
        _define_property(this, "renderFreeTierTotalPrice", ()=>{
            const { translateOrTranslateWithParam, hasStudentPlan } = this.props;
            const billingMessage = hasStudentPlan ? "pricingPage.freeForDurationOfStudies" : "pricingPage.freeForever";
            return /*#__PURE__*/ _jsx(StyledFreeTierTotalPrice, {
                className: "price-card-free-tier-total-price",
                dangerouslySetInnerHTML: {
                    __html: translateOrTranslateWithParam(billingMessage)
                }
            });
        });
        _define_property(this, "getTotalPriceText", ()=>{
            const { plan, translateOrTranslateWithParam, hasStudentPlan, cookies, forcePricing, abTests } = this.props;
            const clusterConfig = this.getClusterConfigOrDefault();
            const { tier } = plan;
            const isFree = tier === TIERS.FREE;
            const regularBoldText = translateOrTranslateWithParam({
                key: "pricingPage.total.withNumber",
                param: this.renderFullPrice()
            });
            const yearlyBoldText = translateOrTranslateWithParam({
                key: "pricingPage.total.withNumber.yearly",
                param: this.renderFullPrice()
            });
            const freeTierBoldText = translateOrTranslateWithParam({
                key: "pricingPage.upToXSeats",
                param: getFreeTierSeatsCountByAbTest({
                    cookies,
                    abTests,
                    forcePricing
                })
            });
            const studentPlanBoldText = translateOrTranslateWithParam({
                key: "pricingPage.freeForDurationOfStudies"
            });
            if (isFree) {
                return hasStudentPlan ? studentPlanBoldText : freeTierBoldText;
            }
            return shouldShowYearlyPrices(clusterConfig, abTests, cookies, forcePricing) ? yearlyBoldText : regularBoldText;
        });
        _define_property(this, "renderRegularTotalPriceSection", ()=>{
            const { plan, billingPeriod, translateOrTranslateWithParam } = this.props;
            const { tier } = plan;
            const isFree = tier === TIERS.FREE;
            const boldText = this.getTotalPriceText();
            return /*#__PURE__*/ _jsxs(StyledRegularTotalPriceSection, {
                className: "price-card-total-price",
                tabIndex: 0,
                onMouseDown: preventFocusOnMouseDown,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "total-price-text",
                        children: /*#__PURE__*/ _jsx("span", {
                            className: "value",
                            style: {
                                color: colors["black"]
                            },
                            children: boldText
                        })
                    }),
                    !isFree && /*#__PURE__*/ _jsx("span", {
                        className: "billing-message",
                        children: translateOrTranslateWithParam(PERIOD_TEXTS[billingPeriod].billed)
                    })
                ]
            });
        });
        _define_property(this, "renderPerSeatPerMonthText", ()=>{
            const { plan, translateOrTranslateWithParam, hasStudentPlan, cookies, forcePricing, abTests } = this.props;
            const clusterConfig = this.getClusterConfigOrDefault();
            const { tier } = plan;
            const isFree = tier === TIERS.FREE && !hasStudentPlan;
            const firstLine = isFree ? "pricingPage.free" : "pricingPage.seatOver";
            const secondLine = isFree ? "pricingPage.forever" : shouldShowYearlyPrices(clusterConfig, abTests, cookies, forcePricing) ? "pricingPage.year" : "pricingPage.month";
            return /*#__PURE__*/ _jsxs("span", {
                className: "per-user-per-month",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: translateOrTranslateWithParam(firstLine)
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: translateOrTranslateWithParam(secondLine)
                    })
                ]
            });
        });
        _define_property(this, "renderPriceSection", ()=>{
            const { plan } = this.props;
            return /*#__PURE__*/ _jsxs(StyledPerSeatPerMonthText, {
                children: [
                    this.renderPricePerSeatWithoutDiscountIfNeeded(),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "price-card-price-per-month",
                        style: {
                            color: colors["black"]
                        },
                        tabIndex: 0,
                        onMouseDown: preventFocusOnMouseDown,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "plan-price",
                                style: {
                                    color: getPlanColor(plan, true)
                                },
                                children: this.renderPricePerSeat()
                            }),
                            this.renderPerSeatPerMonthText()
                        ]
                    }),
                    this.renderRegularTotalPriceSection()
                ]
            });
        });
        _define_property(this, "getTierFeatureAriaLabel", (feature)=>{
            const { plan, translateOrTranslateWithParam } = this.props;
            const { displayName } = plan;
            return `${translateOrTranslateWithParam(displayName)}: ${sanitizeHtml(translateOrTranslateWithParam(feature.name), {
                allowedTags: [],
                allowedAttributes: []
            })}`;
        });
        _define_property(this, "renderTierFeature", (feature, index)=>{
            const { billingPeriod, plan, translateOrTranslateWithParam, showFeatureCheckbox, isMobile } = this.props;
            const { tier } = plan;
            const isAI = isAIFeature(feature);
            return /*#__PURE__*/ _jsxs(StyledTierFeature, {
                className: "price-card-tier-feature",
                children: [
                    showFeatureCheckbox && /*#__PURE__*/ _jsx(Check, {}),
                    isAI && /*#__PURE__*/ _jsx(MondayAiIcon, {}),
                    /*#__PURE__*/ _jsx("div", {
                        className: "name-and-label",
                        children: /*#__PURE__*/ _jsx("span", {
                            className: "tier-feature-name",
                            dangerouslySetInnerHTML: {
                                __html: translateOrTranslateWithParam(feature.name)
                            },
                            "aria-label": this.getTierFeatureAriaLabel(feature),
                            role: "region",
                            onMouseDown: preventFocusOnMouseDown
                        })
                    }),
                    feature.text && /*#__PURE__*/ _jsx("span", {
                        className: "tier-feature-description",
                        children: /*#__PURE__*/ _jsx(FeatureInfoWithTooltip, {
                            feature: feature,
                            placement: "tier-value-card",
                            tier: tier,
                            index: index,
                            isMobile: isMobile,
                            getRegularOrTranslatedText: translateOrTranslateWithParam
                        })
                    })
                ]
            }, index + "_" + billingPeriod);
        });
        _define_property(this, "shouldIncreaseSeparatorTopMargin", ()=>{
            // fixes a mobile bug where the separator is higher in the enterprise plan than elsewhere.
            const { plan, userCount, cookies, forcePricing, abTests } = this.props;
            const clusterConfig = this.getClusterConfigOrDefault();
            const { tier } = plan;
            return tier === TIERS.ENTERPRISE && !shouldSeeContactUs(TIERS.BASIC, userCount, {
                clusterConfig,
                cookies,
                forcePricing,
                abTests
            });
        });
        _define_property(this, "getRegularOrLineDropText", (originalKey)=>{
            return this.hasMoreThanFourPlans() ? `${originalKey}.withLineDrop` : originalKey;
        });
        _define_property(this, "onPurchaseNowClick", (e)=>{
            const { plan, onPurchaseNowClicked } = this.props;
            const { currentTarget } = e;
            const purchaseNowLinkParent = currentTarget === null || currentTarget === void 0 ? void 0 : currentTarget.closest(`div.${PRICE_CARD_PRICE_CONTAINER_CLASS}`);
            const signupButton = purchaseNowLinkParent === null || purchaseNowLinkParent === void 0 ? void 0 : purchaseNowLinkParent.querySelector("button");
            onPurchaseNowClicked({
                tier: plan.tier,
                target: signupButton
            });
        });
        _define_property(this, "shouldShowCTA", ()=>{
            const { plan, hidePlanTryForFreeButton, isMobileOrTabletBrowser } = this.props;
            if (plan.tier === TIERS.ENTERPRISE) return true;
            if (isMobileOrTabletBrowser) return false;
            return !hidePlanTryForFreeButton;
        });
        _define_property(this, "renderLearnMoreVideoCTAOrPlaceholder", ()=>{
            const { plan, hidePlanTryForFreeButton, localeId } = this.props;
            const hasVideo = hasLearnMoreVideo(plan.tier);
            if (!hasVideo && !hidePlanTryForFreeButton) return null;
            return /*#__PURE__*/ _jsx("div", {
                className: "tier-learn-more-button",
                children: hasVideo && /*#__PURE__*/ _jsx(YouTubePopUpButton, {
                    isWithIcon: true,
                    isWithIconInsteadOfArrow: true,
                    withoutUnderline: true,
                    minPadding: true,
                    outline: true,
                    isWithArrow: false,
                    color: WORK_OS_IRIS_COLOR_NAME,
                    iconColor: "outline-dark-blue",
                    buttonText: this.getLearnMoreButtonText(),
                    videoId: getVideoIdByLocaleAndTier(localeId, plan.tier),
                    buttonDisplayType: hidePlanTryForFreeButton ? SECONDARY_BUTTON_TYPE : REGULAR_BUTTON_TYPE,
                    extraEventData: {
                        buttonName: "hp_pp_tier_learn_more",
                        tier: plan.tier,
                        locale: localeId
                    }
                })
            });
        });
    }
}
export default withGenericConfig(PriceAndFeaturesCard);
